<template>
  <v-dialog v-model="isOpen" max-width="800" persistent>
    <v-card class="agent-contact-modal">
      <!-- Header -->
      <v-card-title class="modal-header">
        <div class="header-content">
          <div class="agent-info">
            <div class="agent-avatar">
              <i class="material-icons-round">real_estate_agent</i>
            </div>
            <div class="agent-details">
              <h3 class="agent-name">Sophia</h3>
              <p class="agent-title">HomeSyncX™ Real Estate Agent Assistant</p>
            </div>
          </div>
          <div class="header-actions">
            <!-- Close Button -->
            <v-btn
              icon
              @click="closeModal"
              class="close-btn"
            >
              <v-icon class="material-icons-round">close</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-title>

      <!-- Content -->
      <v-card-text class="modal-content">
        <!-- Chat Mode -->
        <div class="chat-container">
          <div class="chat-messages" ref="chatMessages">
            <div
              v-for="(message, index) in messages"
              :key="index"
              :class="['message', message.sender_type === 'user' ? 'user-message' : 'agent-message']"
            >
              <div class="message-content">
                <div class="message-text">{{ message.message }}</div>
                <div class="message-time">{{ formatTime(message.sent_at) }}</div>
              </div>
            </div>
            <div v-if="isTyping" class="typing-indicator">
              <div class="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span class="typing-text">Sophia is typing...</span>
            </div>
          </div>
          
          <div class="chat-input">
            <!-- Photo Upload Section (shown when in photo_upload state) -->
            <div v-if="signupState === 'photo_upload' && !uploadedPhoto" class="photo-upload-section">
              <input
                type="file"
                ref="photoInput"
                accept="image/*"
                @change="handlePhotoSelect"
                style="display: none"
              />
              <v-btn
                color="blue"
                dark
                @click="$refs.photoInput.click()"
                class="upload-btn"
                block
              >
                <i class="material-icons-round mr-2">add_photo_alternate</i>
                Upload Profile Photo
              </v-btn>
              <p class="upload-hint">Click to browse or drag and drop an image</p>
            </div>

            <!-- Photo Preview (shown after photo selected) -->
            <div v-else-if="signupState === 'photo_upload' && uploadedPhoto" class="photo-preview-section">
              <div class="photo-preview">
                <img :src="photoPreview" alt="Profile Preview" />
                <v-btn
                  icon
                  small
                  color="error"
                  @click="removePhoto"
                  class="remove-photo-btn"
                >
                  <i class="material-icons-round">close</i>
                </v-btn>
              </div>
              <v-btn
                color="blue"
                @click="uploadPhoto"
                :loading="uploadingPhoto"
                block
                dark
                class="mt-3"
              >
                <i class="material-icons-round mr-2">cloud_upload</i>
                Confirm & Upload
              </v-btn>
            </div>

            <!-- Regular Text Input -->
            <div v-else-if="signupState !== 'complete'" class="input-wrapper">
              <v-text-field
                v-model="currentMessage"
                placeholder="Type your message..."
                outlined
                dense
                hide-details
                @keyup.enter="sendMessage"
                :disabled="isTyping"
                class="message-input"
              >
                <template v-slot:append>
                  <v-btn
                    icon
                    color="primary"
                    @click="sendMessage"
                    :disabled="!currentMessage.trim() || isTyping"
                  >
                    <i class="material-icons-round">send</i>
                  </v-btn>
                </template>
              </v-text-field>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>

export default {
  name: 'HomeSyncAgentModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'chat'
    },
    userId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      messages: [],
      currentMessage: '',
      isTyping: false,
      signupSessionId: null,
      isInSignupFlow: false,
      signupState: 'greeting',
      collectedData: {},
      registrationCompleted: false,
      uploadedPhoto: null,
      photoPreview: null,
      uploadingPhoto: false
    }
  },
  mounted() {
    this.initializeChat()
  },
  methods: {
    async initializeChat() {
      try {
        await this.initializeHomeSyncAgentSignup()
      } catch (error) {
        console.error('Failed to initialize chat:', error)
      }
    },

    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    },

    closeModal() {
      this.$emit('close')
    },

    async sendMessage() {
      if (!this.currentMessage.trim() || this.isTyping) return

      const userMessage = this.currentMessage.trim()
      this.currentMessage = ''

      // Add user message to chat
      this.addMessage({
        sender_type: 'user',
        message: userMessage,
        sent_at: new Date().toISOString()
      })

      // Process message through HomeSyncX agent signup
      await this.handleHomeSyncAgentSignup(userMessage)
    },

    addMessage(message) {
      this.messages.push(message)
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },

    scrollToBottom() {
      const container = this.$refs.chatMessages
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    async handleHomeSyncAgentSignup(userMessage) {
      try {
        if (!this.isInSignupFlow) {
          await this.initializeHomeSyncAgentSignup()
        }

        await this.processHomeSyncAgentMessage(userMessage)

      } catch (error) {
        console.error('❌ Error in HomeSyncX agent signup:', error)
        this.addMessage({
          sender_type: 'agent',
          message: 'Sorry, there was an error processing your request. Please try again.',
          sent_at: new Date().toISOString()
        })
      } finally {
        this.isTyping = false
      }
    },

    async initializeHomeSyncAgentSignup() {
      try {
        const response = await axios.post('/api/homesync-agent-signup/init')

        if (response.data.flag === 1) {
          this.signupSessionId = response.data.session_id
          this.isInSignupFlow = true
          
          // Add initial greeting
          this.addMessage({
            sender_type: 'agent',
            message: response.data.greeting,
            sent_at: new Date().toISOString()
          })
          
          console.log('✅ HomeSyncX agent signup initialized:', this.signupSessionId)
        } else {
          throw new Error('Failed to initialize HomeSyncX agent signup')
        }
      } catch (error) {
        console.error('❌ Error initializing HomeSyncX agent signup:', error)
        throw error
      }
    },

    async processHomeSyncAgentMessage(userMessage) {
      try {
        this.isTyping = true
        console.log('📤 Sending message to API:', userMessage)

        const response = await axios.post('/api/homesync-agent-signup/message', {
          session_id: this.signupSessionId,
          message: userMessage
        })

        console.log('📥 API Response:', response.data)

        if (response.data.flag === 1) {
          this.signupState = response.data.conversation_state
          this.collectedData = response.data.collected_data

          console.log('📊 Updated state:', this.signupState)
          console.log('📊 Collected data:', this.collectedData)

          // Add AI response
          this.addMessage({
            sender_type: 'agent',
            message: response.data.message,
            sent_at: new Date().toISOString()
          })

          // Check if registration is complete
          if (response.data.is_complete && !this.registrationCompleted) {
            console.log('✅ Registration complete, calling completeHomeSyncAgentSignup...')
            await this.completeHomeSyncAgentSignup()
          }
        } else {
          throw new Error('Failed to process message')
        }
      } catch (error) {
        console.error('❌ Error processing message:', error)
        throw error
      } finally {
        this.isTyping = false
      }
    },

    async completeHomeSyncAgentSignup() {
      try {
        this.registrationCompleted = true
        console.log('📤 Completing HomeSyncX agent signup...')

        const formData = new FormData()
        formData.append('session_id', this.signupSessionId)

        if (this.userId) {
          formData.append('ref_id', this.userId)
        }

        if (this.uploadedPhoto) {
          formData.append('file_photo', this.uploadedPhoto)
        }

        const response = await axios.post('/api/homesync-agent-signup/complete', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        const responseData = response.data

        console.log('📥 Final response data:', responseData)

        if (responseData.flag === 1) {
          console.log('✅ HomeSyncX agent registration completed:', responseData)

          const domain = responseData.domain
          const referralLink = `https://${domain}.homesyncx.com`

          // Change state to complete to hide upload buttons
          this.signupState = 'complete'

          this.addMessage({
            sender_type: 'agent',
            message: `All set! 🎯 I've created your digital referral card.\n\nHere's your referral link:\n👉 ${referralLink}\n\nNow, just share me (Sophia) with other real estate professionals. I'll explain everything and sign them up under you. The bigger your network, the more you'll earn.`,
            sent_at: new Date().toISOString()
          })

          this.$toast.success('Welcome to HomeSyncX™! Your referral card has been created.')

          this.$emit('signup-complete', {
            user: responseData.data,
            referral_link: referralLink,
            domain: domain
          })
        } else if (responseData.flag === 3) {
          this.addMessage({
            sender_type: 'agent',
            message: 'It looks like you already have an account with that email. Would you like to try a different email address?',
            sent_at: new Date().toISOString()
          })
          this.registrationCompleted = false
        } else {
          throw new Error(responseData.message || 'Registration failed')
        }

      } catch (error) {
        console.error('❌ Error completing HomeSyncX agent signup:', error)
        this.addMessage({
          sender_type: 'agent',
          message: 'Sorry, there was an error completing your registration. Please try again.',
          sent_at: new Date().toISOString()
        })
        this.registrationCompleted = false
      }
    },

    handlePhotoSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.uploadedPhoto = file
        this.photoPreview = URL.createObjectURL(file)
      }
    },

    removePhoto() {
      this.uploadedPhoto = null
      this.photoPreview = null
      if (this.$refs.photoInput) {
        this.$refs.photoInput.value = ''
      }
    },

    async uploadPhoto() {
      if (!this.uploadedPhoto) return

      this.uploadingPhoto = true

      try {
        // Simulate upload message
        this.addMessage({
          sender_type: 'user',
          message: '📸 Uploaded profile photo',
          sent_at: new Date().toISOString()
        })

        // Complete the signup with photo
        await this.completeHomeSyncAgentSignup()

      } catch (error) {
        console.error('Error uploading photo:', error)
        this.$toast.error('Failed to upload photo. Please try again.')
      } finally {
        this.uploadingPhoto = false
      }
    }
  }
}
</script>

<style scoped>
.agent-contact-modal {
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #0052CC 0%, #0066FF 100%);
  color: white;
  padding: 20px 24px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.agent-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-avatar i {
  font-size: 32px;
  color: white;
}

.agent-name {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.agent-title {
  margin: 4px 0 0 0;
  font-size: 14px;
  opacity: 0.9;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.close-btn {
  color: white !important;
}

.modal-content {
  padding: 0 !important;
  height: 600px;
  display: flex;
  flex-direction: column;
}

.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: #f5f7fa;
}

.message {
  margin-bottom: 16px;
  display: flex;
}

.user-message {
  justify-content: flex-end;
}

.agent-message {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.user-message .message-content {
  background: #0052CC;
  color: white;
}

.message-text {
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.5;
}

.message-time {
  font-size: 11px;
  opacity: 0.7;
  margin-top: 4px;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border-radius: 12px;
  width: fit-content;
}

.typing-dots {
  display: flex;
  gap: 4px;
}

.typing-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #0052CC;
  animation: typing 1.4s infinite;
}

.typing-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  30% {
    opacity: 1;
    transform: scale(1);
  }
}

.typing-text {
  font-size: 13px;
  color: #666;
}

.chat-input {
  padding: 16px 24px;
  background: white;
  border-top: 1px solid #e0e0e0;
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.photo-upload-section,
.photo-preview-section {
  text-align: center;
}

.upload-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #666;
}

.photo-preview {
  position: relative;
  display: inline-block;
}

.photo-preview img {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  object-fit: cover;
}

.remove-photo-btn {
  position: absolute;
  top: -8px;
  right: -8px;
}
</style>
